import React, { useState, useEffect, useRef } from "react";
import CustomSelect from "../../components/CustomSelect/CustomSelect";
import { useSearchParams } from "react-router-dom";
import useDatepicker from "../../hooks/useDatepicker/useDatepicker";
import Datepicker from "../../components/Datepicker/Datepicker";
import RateBox from "../../components/ratebox/RateBox";
import axios from "axios";
import { Helmet } from "react-helmet";

const Silverrate = () => {
  const container = useRef();
  const { datepicker, handleDateChange } = useDatepicker();
  const [searchParams] = useSearchParams();
  const selectedState = searchParams.get("state") || "Gujarat";
  const [silverRateRaw, setSilverRateRaw] = useState(null);
  const [silverRateChange, setSilverRateChange] = useState(null);

  const formatRate = (value) =>
    value?.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }) ?? "Loading...";

  useEffect(() => {
    const fetchData = async () => {
      try {
        // const res = await axios.get("https://api.gold-api.com/price/XAG");
        // const pricePerOunce = parseFloat(res.data.price);
        // const pricePerGram = (pricePerOunce / 28.3495) * 85;
        // setSilverRateRaw(pricePerGram);

        const res = await axios.get("https://data-asg.goldprice.org/dbXRates/INR");
        const pricePerGram = (res.data.items[0].xagPrice / 31.1034);
        setSilverRateRaw(pricePerGram);

        const priceChange = (res.data.items[0].chgXag / 31.1034);
        setSilverRateChange(priceChange);
      } catch (error) {
        console.error("Error fetching silver rate:", error);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, []);

  const data = [
    {
      type: "Silver /g",
      rate: formatRate(silverRateRaw),
      difference: silverRateChange !== null
        ? `${silverRateChange >= 0 ? "+" : "-"} ₹${Math.abs(silverRateChange).toFixed(2)}`
        : "Loading..."
    },
    {
      type: "Silver /kg",
      rate: silverRateRaw ? formatRate(silverRateRaw * 1000) : "Loading...",
      difference: silverRateChange !== null
        ? `${silverRateChange >= 0 ? "+" : "-"} ₹${Math.abs((silverRateChange * 1000).toFixed(2))}`
        : "Loading..."
    },
  ];

  data.forEach((item) => {
    if (item.difference.trim().startsWith("+")) {
      item.symbol = "up";
    } else if (item.difference.trim().startsWith("-")) {
      item.symbol = "down";
    } else {
      item.symbol = "neutral";
    }
  });

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js";
    script.type = "text/javascript";
    script.async = true;
    script.innerHTML = `
      {
        "allow_symbol_change": true,
        "calendar": false,
        "details": false,
        "hide_side_toolbar": true,
        "hide_top_toolbar": false,
        "hide_legend": false,
        "hide_volume": false,
        "hotlist": false,
        "interval": "D",
        "locale": "en",
        "save_image": true,
        "style": "1",
        "symbol": "FX_IDC:XAGINRK",
        "theme": "light",
        "timezone": "Etc/UTC",
        "backgroundColor": "#ffffff",
        "gridColor": "rgba(46, 46, 46, 0.06)",
        "watchlist": [],
        "withdateranges": false,
        "compareSymbols": [],
        "studies": [],
        "width": "100%",
        "height": 610
      }`;
    container.current.appendChild(script);
  }, []);

  return (
    <>
      <Helmet>
        <title>
          Today Silver Rate in {selectedState} | 1 Gram & 1 Kg Silver Price Live
        </title>

        <meta
          name="description"
          content={`Check today's silver rate in ${selectedState}. Live price for 1 gram and 1 kilogram of silver with daily updates and trading chart.`}
        />
        <meta
          name="keywords"
          content={`silver rate today, ${selectedState} silver price, silver price per gram, silver price per kg, live silver rate India, silver chart, silver price update`}
        />

        {/* Open Graph (Facebook/WhatsApp) */}
        <meta
          property="og:title"
          content={`Today Silver Rate in ${selectedState} | Live Silver Price & Chart`}
        />
        <meta
          property="og:description"
          content={`View today's silver rate in ${selectedState} — live 1g & 1kg silver prices with chart and daily updates.`}
        />
        <meta
          property="og:url"
          content={`https://gold-rate-today.com/silverrate?state=${selectedState}`}
        />
        <meta
          property="og:image"
          content="https://gold-rate-today.com/images/silver-banner.jpg"
        />
        <meta property="og:type" content="website" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={`Today Silver Rate in ${selectedState} | Gold-Rate-Today.com`}
        />
        <meta
          name="twitter:description"
          content="Check live 1 gram and 1 kilogram silver prices with updated charts and trends."
        />
        <meta
          name="twitter:image"
          content="https://gold-rate-today.com/images/silver-banner.jpg"
        />

        <link
          rel="canonical"
          href={`https://gold-rate-today.com/silverrate?state=${selectedState}`}
        />

        {/* JSON-LD Structured Data for SEO */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Silver Rate Today in ${selectedState}",
            "url": "https://gold-rate-today.com/silverrate?state=${selectedState}",
            "description": "Live silver prices per gram and kilogram in ${selectedState}. Updated every few seconds with interactive chart.",
            "publisher": {
              "@type": "Organization",
              "name": "Gold-Rate-Today.com",
              "url": "https://gold-rate-today.com/"
            }
          }
        `}</script>
      </Helmet>
      <section className="rate-page">
        <div className="top-head">
          <h3>Silver Price in {selectedState}</h3>
          <Datepicker datepicker={datepicker} handleDateChange={handleDateChange} />
        </div>
        <CustomSelect />
        <div className="rate-main-box">
          {data.map((item, index) => (
            <RateBox
              key={index}
              type={item.type}
              rate={item.rate}
              difference={item.difference}
              symbol={item.symbol}
            />
          ))}
        </div>
        <div className="intro-content">
          <p>
            <span>Today’s silver rate in {selectedState}</span> is <span>₹{data[0].rate}</span> per gram and <span>₹{data[1].rate}</span> per kilogram.
          </p>
        </div>
        <div className="tradingview-widget-container" ref={container} style={{ padding: "15px" }}>
          <div className="tradingview-widget-container__widget"></div>
          <div className="tradingview-widget-copyright">
            <a href="https://www.tradingview.com/" rel="noopener nofollow" target="_blank">
              <span className="blue-text">Track all markets on TradingView</span>
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default Silverrate;
